import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, Star, X } from 'lucide-react';
import type { SavedLocation, SaveLocationInput } from '../lib/api';
import type { ParcelPanelContext } from '../lib/parcel-panel-context-types';
import type { ParcelPanelResponse } from '../lib/parcel-panel-types';
import { parcelPanelClient } from '../lib/parcel-panel-client';
import { useTheme } from '../context/ThemeContext';
import { ParcelPanelEvidence } from './ParcelPanelEvidence';
import { ParcelTerrainAnalysis } from './ParcelTerrainAnalysis';
import { ParcelEnvironmentAnalysis } from './ParcelEnvironmentAnalysis';
import {
  isScoredPipelineReference, pipelineReferencePresentation, PIPELINE_REFERENCE_FLOW_SOURCE,
} from '../lib/pipeline-reference';
import './ParcelDetail.css';

interface ParcelDetailProps {
  parcelId: string;
  onClose: () => void;
  onParcelCounty?: (county: string | null) => void;
  savedLocation: SavedLocation | undefined;
  onSaveLocation: (input: SaveLocationInput) => Promise<SavedLocation>;
  onDeleteSavedLocation: (id: number) => Promise<void>;
  onShowTerrain?: (center?: [number, number]) => void;
  terrainEnabled?: boolean;
}

interface PanelState {
  parcelId: string;
  snapshot: ParcelPanelResponse | null;
  context: ParcelPanelContext | null;
  loading: boolean;
  contextLoading: boolean;
  error: string | null;
  contextError: boolean;
}

const emptyState = (parcelId: string): PanelState => ({
  parcelId, snapshot: null, context: null,
  loading: true, contextLoading: true, error: null, contextError: false,
});

function number(value: unknown, digits = 2): string {
  if (value == null || value === '' || !Number.isFinite(Number(value))) return 'Unknown';
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: digits });
}

export const ParcelDetail: React.FC<ParcelDetailProps> = ({
  parcelId, onClose, onParcelCounty, savedLocation, onSaveLocation, onDeleteSavedLocation,
  onShowTerrain, terrainEnabled,
}) => {
  const { isDark } = useTheme();
  const [state, setState] = useState<PanelState>(() => emptyState(parcelId));
  const [retry, setRetry] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const savingRef = useRef(false);
  const currentParcelRef = useRef(parcelId);
  currentParcelRef.current = parcelId;
  const selected = state.parcelId === parcelId ? state : emptyState(parcelId);
  const { snapshot } = selected;
  const contextMismatch = Boolean(snapshot && selected.context && snapshot.subject.boundary_revision !== selected.context.boundary_revision);
  const context = contextMismatch ? null : selected.context;
  const enrichment = context?.enrichment || null;
  const gasSupply = context?.gas_supply;
  const parcel = snapshot?.parcel;
  const busy = selected.loading || selected.contextLoading;

  useEffect(() => {
    let active = true;
    setState(previous => previous.parcelId === parcelId
      ? { ...previous, loading: true, contextLoading: true, error: null, contextError: false }
      : emptyState(parcelId));
    setSaveError(null);
    const update = (patch: Partial<PanelState>) => {
      if (active) setState(previous => previous.parcelId === parcelId ? { ...previous, ...patch } : previous);
    };
    parcelPanelClient.snapshot(parcelId)
      .then(value => update({ snapshot: value }))
      .catch(error => update({ error: error instanceof Error ? error.message : 'The saved parcel could not be loaded.' }))
      .finally(() => update({ loading: false }));
    parcelPanelClient.context(parcelId)
      .then(value => update({ context: value }))
      .catch(() => update({ contextError: true }))
      .finally(() => update({ contextLoading: false }));
    return () => { active = false; };
  }, [parcelId, retry]);

  useEffect(() => {
    setElapsed(0);
    if (!busy) return;
    const start = Date.now();
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [parcelId, retry, busy]);

  useEffect(() => {
    onParcelCounty?.(parcel?.county && parcel?.state ? `${parcel.county}, ${parcel.state}` : null);
    return () => onParcelCounty?.(null);
  }, [parcel?.id, parcel?.county, parcel?.state, onParcelCounty]);

  const candidate = snapshot?.gas_candidate;
  const failedSections = Object.entries(snapshot?.section_errors || {});
  const contextIssues = Object.entries(context?.section_errors || {});
  const failed = Boolean(selected.error || selected.contextError || contextMismatch || failedSections.length || contextIssues.length);
  const retryable = Boolean(selected.error || selected.contextError || contextMismatch || [...failedSections, ...contextIssues].some(([, issue]) => issue?.status === 'failed'));
  const reportedArea = number(parcel?.acreage);
  const measuredArea = number(snapshot?.subject.measured_acres);
  const regional = gasSupply?.radii?.find(row => Number(row.radius_miles) === 5);
  const reference = pipelineReferencePresentation(parcel || {});
  const scoredReference = parcel && isScoredPipelineReference(parcel) ? parcel.pipeline_reference : null;

  async function toggleSave() {
    if (!parcel || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    const id = parcel.id;
    try {
      if (savedLocation) await onDeleteSavedLocation(savedLocation.id);
      else await onSaveLocation({
        name: `${parcel.owner_name || 'Parcel'} — ${reportedArea === 'Unknown' ? parcel.id : `${reportedArea} ac`}`.slice(0, 200),
        lat: parcel.lat, lng: parcel.lng, source_type: 'parcel', source_id: parcel.id,
      });
    } catch {
      if (currentParcelRef.current === id) setSaveError('The saved location could not be updated. Try again.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  return (
    <aside className="parcel-panel" data-theme={isDark ? 'dark' : 'light'} aria-label="Parcel analysis" data-parcel-id={parcelId}>
      <header className="panel-masthead">
        <div className="panel-utility">
          <span>Parcel {parcel?.parcel_id_local || parcelId}</span>
          <div className="panel-actions">
            {parcel && <button type="button" onClick={toggleSave} disabled={saving || (!savedLocation && (parcel.lat == null || parcel.lng == null))}
              aria-label={savedLocation ? 'Remove saved parcel' : 'Save parcel'} aria-pressed={Boolean(savedLocation)}>
              <Star size={17} fill={savedLocation ? 'currentColor' : 'none'} />
            </button>}
            <button type="button" onClick={onClose} aria-label="Close parcel panel"><X size={19} /></button>
          </div>
        </div>
        <h1>{parcel?.property_address || (parcel?.county ? `${parcel.county} County parcel` : 'Selected parcel')}</h1>
        <p className="panel-location">{parcel ? [parcel.county, parcel.state].filter(Boolean).join(', ') || 'Location unknown' : 'Loading saved identity'}</p>
        <p className="panel-owner"><span>Assessor owner</span> {parcel?.owner_name || 'Unknown'}</p>
      </header>
      <div className="panel-scroll" key={parcelId}>
        {saveError && <p className="panel-error" role="alert">{saveError}</p>}
        {!snapshot && <div className="panel-opening" role="status">
          {selected.loading ? `Loading saved parcel${elapsed ? ` · ${elapsed} s` : ''}` : selected.error}
          {!selected.loading && <button className="panel-inline-button" type="button" onClick={() => setRetry(value => value + 1)}>Retry saved data</button>}
        </div>}
        {snapshot && parcel && <>
          <div className="panel-acreage" aria-label="Selected parcel area">
            <div><div className="panel-area-number">{reportedArea}{reportedArea !== 'Unknown' && <span> ac</span>}</div><div>Reported acreage<span className="panel-value-note">Saved parcel record</span></div></div>
            <div><div className="panel-area-number">{measuredArea}{measuredArea !== 'Unknown' && <span> ac</span>}</div><div>Mapped footprint<span className="panel-value-note">{snapshot.subject.boundary_status === 'available' ? 'Measured from stored boundary' : 'Usable boundary unavailable'}</span></div></div>
          </div>
          <details className="panel-disclosure panel-analysis-state">
            <summary><span role="status">{busy ? `Loading saved context · ${elapsed} s` : failed ? 'Some saved records are unavailable' : 'Saved results · source dates incomplete'}</span></summary>
            <p className="panel-note">These records retain their source and footprint limits. Opening this panel does not establish current coverage or complete a new site assessment.</p>
            {selected.error && <p className="panel-error">{selected.error} The previous snapshot remains visible.</p>}
            {failedSections.map(([section, issue]) => <p className="panel-note" key={section}>{section.replace(/_/g, ' ')}: {issue?.message}</p>)}
            {selected.contextError && <p className="panel-error">Supporting records could not be loaded.</p>}
            {contextMismatch && <p className="panel-error">The boundary changed between reads. Reload the saved data to align the footprint and supporting records.</p>}
            {contextIssues.map(([section, issue]) => <p className="panel-note" key={section}>{section}: {issue?.message}</p>)}
            {retryable && !busy && <button type="button" className="panel-inline-button" onClick={() => setRetry(value => value + 1)}>Retry saved data</button>}
            <p className="panel-note">Snapshot retrieved {new Date(snapshot.retrieved_at).toLocaleString()}. Retrieval time is not the source date.</p>
          </details>
          <section className="panel-section" aria-label="Gas supply">
            <div className="panel-section-heading"><h2>Gas supply</h2><span>Delivery unconfirmed</span></div>
            <p className="panel-note"><strong>{reference.scoreCopy}.</strong></p>
            {scoredReference && <>
              <table className="panel-facts"><tbody>
                <tr><th scope="row">Scoring pipeline</th><td>{scoredReference.pipeline_name}<span className="panel-value-note">{number(scoredReference.diameter_inches)} in · {number(scoredReference.distance_miles)} mi · {scoredReference.source}</span></td></tr>
                <tr><th scope="row">Modeled gas</th><td>{number(scoredReference.gas_mmscfd)} MMscf/day</td></tr>
                <tr><th scope="row">Continuous net MW</th><td>{number(scoredReference.reference_mw)} MW<span className="panel-value-note">Electrical equivalent if the modeled gas flow is sustained.</span></td></tr>
                <tr><th scope="row">Score assumptions</th><td><a href={PIPELINE_REFERENCE_FLOW_SOURCE} target="_blank" rel="noreferrer">{reference.basisLabel} · equation source</a><span className="panel-value-note">20 mi hydraulic reach · 600→500 psig · recorded diameter as assumed ID · 8,805 Btu/kWh net HHV. Frozen reference inputs, not observed operating conditions.</span></td></tr>
              </tbody></table>
              <p className="panel-note">The 5 GW score ceiling limits points only. Modeled gas and MW remain displayed at their calculated values.</p>
            </>}
            <table className="panel-facts panel-gas"><thead><tr><th scope="col">Mapped candidate</th><th scope="col">Distance</th><th scope="col">Modeled MW</th></tr></thead>
              <tbody><tr><td>{candidate ? <><strong>{candidate.name || 'Unnamed segment'}</strong><span className="panel-value-note">{candidate.operator || 'Operator unknown'}</span><span className="panel-value-note">{candidate.diameter_inches == null ? 'Diameter unknown' : `${number(candidate.diameter_inches)} in`}</span></> : 'Unavailable'}</td>
                <td>{candidate ? `${number(candidate.distance_miles)} mi` : 'Unknown'}</td><td>Unavailable</td></tr></tbody>
            </table>
            <p className="panel-note">{candidate ? 'Boundary-to-segment distance. ' : 'No qualified mapped transmission candidate is available. '}Model inputs and firm delivery are unverified.</p>
            <details className="panel-disclosure">
              <summary>Gas source &amp; regional context</summary>
              <table className="panel-facts"><tbody>
                <tr><th scope="row">Mapped source</th><td>{candidate?.source || 'Unknown'}<span className="panel-value-note">Source release date unverified</span></td></tr>
                <tr><th scope="row">Segment ID</th><td>{candidate?.segment_id || 'Unavailable'}</td></tr>
                <tr><th scope="row">Candidate selection</th><td>Nearest mapped transmission<span className="panel-value-note">Within 10 miles; suitability unconfirmed</span></td></tr>
                <tr><th scope="row">Distance method</th><td>Parcel footprint to mapped line<span className="panel-value-note">An intersection measures zero. Tap location and terms require operator confirmation.</span></td></tr>
                <tr><th scope="row">MW model</th><td>Inputs not verified<span className="panel-value-note">Candidate-specific capacity, utilization period and model version are required.</span></td></tr>
                <tr><th scope="row">Regional well records</th><td>{selected.contextLoading ? 'Loading' : regional ? `${number(regional.well_count, 0)} in the saved 5-mile screen` : 'Unavailable'}</td></tr>
                <tr><th scope="row">Regional gas production</th><td>{regional ? `${number(regional.total_gas_mcf_day, 0)} Mcf/day` : 'Unknown'}<span className="panel-value-note">Reported regional production does not establish parcel supply.</span></td></tr>
              </tbody></table>
              {regional && <p className="panel-note">Saved calculation: {regional.computed_at || 'date unknown'}. Production reporting period and currentness remain unverified.</p>}
            </details>
          </section>
          <ParcelEnvironmentAnalysis subject={snapshot.subject} onBoundaryChanged={() => setRetry(value => value + 1)} />
          <ParcelPanelEvidence parcel={parcel} listings={snapshot.listings} enrichment={enrichment}
            sectionErrors={snapshot.section_errors} enrichmentLoading={selected.contextLoading} enrichmentError={selected.contextError || contextMismatch}
            contextErrors={context?.section_errors} environmentalRecords={snapshot.environmental_records}
            onShowTerrain={onShowTerrain ? () => onShowTerrain(
              parcel.lng != null && parcel.lat != null && Number.isFinite(Number(parcel.lng)) && Number.isFinite(Number(parcel.lat))
                ? [Number(parcel.lng), Number(parcel.lat)] : undefined,
            ) : undefined} terrainEnabled={terrainEnabled} />
          <ParcelTerrainAnalysis subject={snapshot.subject} onBoundaryChanged={() => setRetry(value => value + 1)} />
          <details className="panel-disclosure">
            <summary>Selected footprint &amp; record identity</summary>
            <table className="panel-facts"><tbody>
              <tr><th scope="row">Parcel ID</th><td>{parcel.id}</td></tr>
              <tr><th scope="row">Boundary revision</th><td>{snapshot.subject.boundary_revision || 'Unavailable'}</td></tr>
              <tr><th scope="row">Measurement</th><td>Stored polygon, geodesic area<span className="panel-value-note">Reported acreage may use a different legal or assessor basis. Neither establishes title.</span></td></tr>
              <tr><th scope="row">Saved screen identity</th><td>Boundary linkage unverified<span className="panel-value-note">The current boundary revision does not retroactively verify saved screening inputs.</span></td></tr>
            </tbody></table>
          </details>
        </>}
      </div>
      <footer className="panel-footer">
        <span>Pre-feasibility screening</span>
        <div><a href={`/briefing/${parcelId}`} target="_blank" rel="noopener noreferrer">Open briefing <ExternalLink size={13} /></a>
          <a className="panel-v2-link" href={`/briefing/${parcelId}/v2`} target="_blank" rel="noopener noreferrer">v2</a></div>
      </footer>
    </aside>
  );
};
